import {
  AppInsightsContext,
  AppInsightsErrorBoundary,
  type ReactPlugin as AppInsightsReactPlugin
} from "@microsoft/applicationinsights-react-js";
import { type PropsWithChildren, useEffect, useState } from "react";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { MantineProvider } from "@mantine/core";
import { getRuntimeSettings } from "@/api/runtimeSettings/getRuntimeSettings";
import { LoadingPane } from "@/components/LoadingPane";
import globalTheme from "@/theme";

export interface AppInsightsProviderProps {
  appInsightsReactPlugin: AppInsightsReactPlugin;
}
export const AppInsightsProvider = ({
  appInsightsReactPlugin: reactPlugin,
  children
}: PropsWithChildren<AppInsightsProviderProps>) => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeAppInsights = async () => {
      try {
        // it is unfortunate that this has to get the runtime setting outside
        // of tanstack query, but it's a chicken-egg nesting problem.
        const runtimeSettings = await getRuntimeSettings();

        if (runtimeSettings.appInsights) {
          const appInsights = new ApplicationInsights({
            config: {
              connectionString: runtimeSettings.appInsights,
              extensions: [reactPlugin]
            }
          });

          appInsights.loadAppInsights();
          appInsights.trackPageView();
        }

        setIsInitialized(true);
      } catch (error) {
        console.error("Failed to initialize Application Insights:", error);
        // Still set initialized to true to prevent infinite loading
        setIsInitialized(true);
      }
    };

    void initializeAppInsights();
  }, [reactPlugin]);
  try {
    return !isInitialized ? (
      <MantineProvider theme={globalTheme}>
        <LoadingPane />
      </MantineProvider>
    ) : (
      <AppInsightsErrorBoundary
        appInsights={reactPlugin}
        onError={() => (
          <div>An unrouted error occurred and has been reported.</div>
        )}
      >
        <AppInsightsContext value={reactPlugin}>{children}</AppInsightsContext>
      </AppInsightsErrorBoundary>
    );
  } catch (e: unknown) {
    const message =
      e instanceof Error
        ? e.message
        : typeof e === "string"
          ? e
          : JSON.stringify(e);
    return (
      <div>
        A profoundly uncaught error occurred and could not be logged: <br />
        {message}
      </div>
    );
  }
};
